if (!customElements.get('ugc-slider')) {
  customElements.define('ugc-slider', class extends HTMLElement {
    connectedCallback() {
      this.buttons = this.querySelectorAll('[data-ugc-watch-index]');
      this.dialog = this.querySelector('[data-ugc-dialog]');
      this.dialogCloseButton = this.dialog.querySelector('[data-ugc-dialog-close]');
      this.volumeButton = this.dialog.querySelector('.ugc__volume');
      this.ugcSlidesCount = this.querySelector('.ugc__slideshow').dataset.slidesCount;
      this.ugcMainElement = this.querySelector('.ugc__main');
      this.mainSliderElement = this.querySelector('.ugc__main-slider');
      this.videos = this.ugcMainElement ? this.ugcMainElement.querySelectorAll('.ugc__video') : [];
      this.autoplayInterval = null;
      this.mainSlider = null;
      this.dialogSlider = null;
      this.dialogSliderThumbnails = null;
      this.currentSlideIndex = 0;
      this.clueElement = this.dialog.querySelector('.ugc__clue');
      this.isSyncing = false;
      this.isButtonClick = false;
      this.mainSliderInView = false;
      this.visibilityObserver = null;
      this.initSlider();
      this.setupEventListeners();
    }

    initSlider() {
      this.mainSlider = new Swiper(this.querySelector('.ugc__main-slider'), {
        slidesPerView: 2.2,
        spaceBetween: 16,
        centeredSlides: true,
        centeredSlidesBounds: true,
        freeMode: true,
        navigation: {
          nextEl: this.querySelector('.ugc__main .ugc__button-next'),
          prevEl: this.querySelector('.ugc__main .ugc__button-prev'),
        },
        breakpoints: {
          750: {
            slidesPerView: 5,
            centeredSlides: false,
          }
        },
      });

      this.dialogSliderThumbnails = new Swiper(this.dialog.querySelector('.ugc__dialog-slider'), {
        slidesPerView: 'auto',
        spaceBetween: 100,
        centeredSlides: true,
        grabCursor: true,
        navigation: {
          nextEl: this.dialog.querySelector('.ugc__dialog .ugc__button-next'),
          prevEl: this.dialog.querySelector('.ugc__dialog .ugc__button-prev'),
        },
        on: {
          slideChange: () => {
            if (!this.isSyncing) {
              this.isSyncing = true;
              this.dialogSlider.slideTo(this.dialogSliderThumbnails.activeIndex);
              this.isSyncing = false;
            }
          },
        },
      });

      this.dialogSlider = new Swiper(this.dialog.querySelector('.ugc__dialog-slideshow'), {
        slidesPerView: 1,
        direction: "vertical",
        on: {
          slideChange: () => {
            this.stopAllVideosInContainer(this.dialog);
            const activeSlide = this.dialogSlider.slides[this.dialogSlider.activeIndex];
            if (activeSlide) {
              const video = activeSlide.querySelector('.ugc__video');
              this.playVideo(video);
            }
            if (!this.isSyncing) {
              this.isSyncing = true;
              this.dialogSliderThumbnails.slideTo(this.dialogSlider.activeIndex);
              this.isSyncing = false;
            }
          },
        }
      });
      this.initVisibilityHandling();
    }

    startAutoplay() {
      if (!this.mainSlider) {
        return;
      }

      if (!this.mainSliderInView) {
        return;
      }

      if (this.autoplayInterval) {
        return;
      }

      this.mainSlider.slideTo(this.currentSlideIndex);
      this.playVideoAtIndex(this.currentSlideIndex);
      this.autoplayInterval = setInterval(() => {
        this.nextSlide();
      }, 5000);
    }

    pauseAutoplay() {
      this.clearAutoplayInterval();
      this.stopAllVideos();
    }

    clearAutoplayInterval() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    }

    nextSlide() {
      this.currentSlideIndex++;
      
      if (this.currentSlideIndex >= Number(this.ugcSlidesCount)) {
        this.currentSlideIndex = 0;
        this.mainSlider.slideTo(0);
        this.playVideoAtIndex(0);
      } else {
        this.mainSlider.slideTo(this.currentSlideIndex);
        this.playVideoAtIndex(this.currentSlideIndex);
      }
    }

    playVideoAtIndex(index) {
      this.stopAllVideos();
      const slide_main = this.mainSlider.slides[index];
      if (!slide_main) return;
      
      const video = slide_main.querySelector('.ugc__video');
      if (video) {
        video.currentTime = 0;
        video.play().catch(err => console.log('Video play failed:', err));
        this.currentVideo = video;
      }
    }

    stopAllVideos() {
      this.videos.forEach(video => {
        video.pause();
        video.currentTime = 0;
      });
    }

    initVisibilityHandling() {
      const target = this.mainSliderElement || (this.mainSlider ? this.mainSlider.el : null);

      if (!target) {
        this.mainSliderInView = true;
        this.startAutoplay();
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.mainSliderInView = this.calculateMainSliderVisibility(target);
        if (this.mainSliderInView) {
          this.startAutoplay();
        }
        return;
      }

      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const isVisible = entry.isIntersecting;
          this.mainSliderInView = isVisible;
          if (isVisible) {
            this.startAutoplay();
          } else {
            this.pauseAutoplay();
          }
        });
      }, {
        threshold: 0.35,
      });

      this.visibilityObserver.observe(target);

      const initiallyVisible = this.calculateMainSliderVisibility(target);
      this.mainSliderInView = initiallyVisible;

      if (initiallyVisible) {
        this.startAutoplay();
      }
    }

    calculateMainSliderVisibility(element) {
      if (!element || typeof element.getBoundingClientRect !== 'function') {
        return false;
      }

      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;

      return rect.top < windowHeight && rect.bottom > 0;
    }

    playVideo(video) {
      if (!video) return;
      video.currentTime = 0;
      video.play().catch(err => console.log('Video play failed:', err));
      video.classList.add('playing');
      this.updateProgressBar(video);
      if (video.closest('[data-ugc-dialog]') && !video.hasAttribute('data-ended-handler')) {
        video.setAttribute('data-ended-handler', 'true');
        video.addEventListener('ended', () => {
          if (this.dialogSlider.activeIndex < this.dialogSlider.slides.length - 1) {
            this.dialogSlider.slideNext();
          } else {
            this.dialogSlider.slideTo(0);
          }
        });
      }
    }

    stopVideo(video) {
      if (!video) return;
      video.pause();
      video.currentTime = 0;
      video.classList.remove('playing');
      
      if (video._progressHandler) {
        video.removeEventListener('timeupdate', video._progressHandler);
        video._progressHandler = null;
      }
      
      const slide = video.closest('.ugc__slide-dialog');
      if (slide) {
        const progress = slide.querySelector('.ugc__progress');
        if (progress) {
          progress.value = 0;
        }
      }
    }

    stopAllVideosInContainer(container) {
      const videos = container.querySelectorAll('.ugc__video');
      videos.forEach(video => this.stopVideo(video));
    }

    updateProgressBar(video) {
      if (!video) return;
      
      const slide = video.closest('.ugc__slide-dialog');
      if (!slide) return;
      
      const progress = slide.querySelector('.ugc__progress');
      if (!progress) return;
      
      if (video._progressHandler) {
        video.removeEventListener('timeupdate', video._progressHandler);
      }
      
      const updateProgress = () => {
        if (video.duration && !video.paused) {
          const percent = (video.currentTime / video.duration) * 100;
          progress.value = percent;
          
          if (video.ended) {
            progress.value = 0;
          }
        }
      };
      
      video._progressHandler = updateProgress;
      video.addEventListener('timeupdate', updateProgress);
      
      video.addEventListener('loadedmetadata', () => {
        progress.value = 0;
      });
    }

    setupEventListeners() {
      this.buttons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          
          this.clearAutoplayInterval();
          this.stopAllVideos();
          
          const slideIndex = parseInt(button.dataset.ugcWatchIndex);
          if (this.dialogSlider && !isNaN(slideIndex)) {
            this.isButtonClick = true;
            this.dialogSlider.slideTo(slideIndex);
            this.dialog.showModal();
            
            setTimeout(() => {
              const activeSlide = this.dialogSlider.slides[slideIndex];
              if (activeSlide) {
                const video = activeSlide.querySelector('.ugc__video');
                this.playVideo(video);
              }
              this.isButtonClick = false;
            }, 100);
          }
          
          this.showClue();
        });
      });


      this.dialogCloseButton.addEventListener('click', () => {
        if (this.dialogSlider) {
          this.currentSlideIndex = this.dialogSlider.activeIndex;
          this.mainSlider.slideTo(this.currentSlideIndex);
          this.stopAllVideosInContainer(this.dialog);
        }
        
        const dialogVideos = this.dialog.querySelectorAll('.ugc__video');
        dialogVideos.forEach(video => {
          video.muted = false;
        });
        
        if (this.volumeButton) {
          this.volumeButton.classList.remove('ugc__volume--off');
          this.volumeButton.setAttribute('aria-pressed', 'false');
        }
        
        this.dialog.close();
        this.hideClue();
        this.startAutoplay();
      });

      const playButton = this.dialog.querySelector('.ugc__dialog [data-ugc-watch]');
      if (playButton) {
        let startY = 0;
        let isSwipe = false;
        
        playButton.addEventListener('click', (e) => {
          if (!isSwipe) {
            e.stopPropagation();
            e.preventDefault();
            e.stopImmediatePropagation();
            
            if (this.dialogSlider) {
              const activeSlide = this.dialogSlider.slides[this.dialogSlider.activeIndex];
              if (activeSlide) {
                const video = activeSlide.querySelector('.ugc__video');
                if (video) {
                  this.currentVideo = video;
                  this.toggleCurrentVideo();
                }
              }
            }
          }
        });
        
        playButton.addEventListener('touchstart', (e) => {
          startY = e.touches[0].clientY;
          isSwipe = false;
        });
        
        playButton.addEventListener('touchmove', (e) => {
          if (!startY) return;
          
          const currentY = e.touches[0].clientY;
          const diffY = startY - currentY;
          
          if (Math.abs(diffY) > 50) {
            isSwipe = true;
          }
        });
        
        playButton.addEventListener('touchend', (e) => {
          if (!startY || !isSwipe) {
            isSwipe = false;
            return;
          }
          
          const endY = e.changedTouches[0].clientY;
          const diffY = startY - endY;
          
          if (this.dialogSlider) {
            const currentIndex = this.dialogSlider.activeIndex;
            const totalSlides = this.dialogSlider.slides.length;
            
            if (diffY > 50) {
              if (currentIndex < totalSlides - 1) {
                this.dialogSlider.slideNext();
              } else {
                this.dialogSlider.slideTo(0);
              }
            } else if (diffY < -50) {
              if (currentIndex > 0) {
                this.dialogSlider.slidePrev();
              } else {
                this.dialogSlider.slideTo(totalSlides - 1);
              }
            }
          }
          
          startY = 0;
          isSwipe = false;
        });
      }


      if (this.volumeButton) {
        this.volumeButton.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          
          const dialogVideos = this.dialog.querySelectorAll('.ugc__video');
          const firstVideo = dialogVideos[0];
          
          if (firstVideo) {
            const newMutedState = !firstVideo.muted;
            
            dialogVideos.forEach(video => {
              video.muted = newMutedState;
            });
            
            this.volumeButton.classList.toggle('ugc__volume--off', newMutedState);
            this.volumeButton.setAttribute('aria-pressed', String(newMutedState));
          }
        });
      }
    }

    toggleCurrentVideo() {
      if (!this.currentVideo) return;

      if (this.currentVideo.paused) {
        this.currentVideo.play();
        this.currentVideo.classList.add('playing');
        this.updateProgressBar(this.currentVideo);
      } else {
        this.currentVideo.pause();
        this.currentVideo.classList.remove('playing');
      }
    }

    showClue() {
      if (!this.clueElement) return;
      setTimeout(() => {
        this.clueElement.classList.add('hidden');
      }, 3000);
    }

    hideClue() {
      if (!this.clueElement) return;
      this.clueElement.classList.remove('hidden');
    }

    disconnectedCallback() {
      this.clearAutoplayInterval();
      this.mainSliderInView = false;
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
        this.visibilityObserver = null;
      }
    }
  });
}